/**
 * TypeScript types for the Annotation/Issues system.
 *
 * Maps to backend schemas in app/schemas/annotation.py
 */

// =============================================================================
// Enums
// =============================================================================

export type AnnotationType = 'issue' | 'self_note' | 'role_comment'

export type IssueStatus = 'open' | 'in_review' | 'query_raised' | 'resolved' | 'closed'

export type IssueCategory =
  | 'missing_data'
  | 'data_discrepancy'
  | 'out_of_range'
  | 'protocol_deviation'
  | 'eligibility_issue'
  | 'ae_related'
  | 'safety_signal'
  | 'query'
  | 'data_entry_error'
  | 'duplicate_record'
  | 'inconsistent_data'
  | 'timeliness'
  | 'source_verification'
  | 'general'

export type IssuePriority = 'low' | 'medium' | 'high' | 'critical'

export type LinkType = 'duplicate' | 'related'

// =============================================================================
// Display Labels
// =============================================================================

export const ISSUE_STATUS_LABELS: Record<IssueStatus, string> = {
  open: 'Open',
  in_review: 'In Review',
  query_raised: 'Query Raised',
  resolved: 'Resolved',
  closed: 'Closed',
}

export const ISSUE_CATEGORY_LABELS: Record<IssueCategory, string> = {
  missing_data: 'Missing Data',
  data_discrepancy: 'Data Discrepancy',
  out_of_range: 'Out of Range',
  protocol_deviation: 'Protocol Deviation',
  eligibility_issue: 'Eligibility Issue',
  ae_related: 'AE Related',
  safety_signal: 'Safety Signal',
  query: 'Query',
  data_entry_error: 'Data Entry Error',
  duplicate_record: 'Duplicate Record',
  inconsistent_data: 'Inconsistent Data',
  timeliness: 'Timeliness',
  source_verification: 'Source Verification',
  general: 'General',
}

export const ISSUE_PRIORITY_LABELS: Record<IssuePriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

export const ANNOTATION_TYPE_LABELS: Record<AnnotationType, string> = {
  issue: 'Issue',
  self_note: 'Self Note',
  role_comment: 'Role Comment',
}

export const LINK_TYPE_LABELS: Record<LinkType, string> = {
  duplicate: 'Duplicate',
  related: 'Related',
}

// Status colors for UI
export const ISSUE_STATUS_COLORS: Record<IssueStatus, string> = {
  open: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  in_review: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  query_raised: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  resolved: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
}

export const ISSUE_PRIORITY_COLORS: Record<IssuePriority, string> = {
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
}

// =============================================================================
// Base Types
// =============================================================================

export interface UserBrief {
  id: string
  email: string
  fullName: string
}

// =============================================================================
// Annotation Types
// =============================================================================

export interface AnnotationBase {
  datasetName: string
  recordKey: Record<string, unknown>
  fieldName?: string | null
  content: string
  sourceModuleKey?: string | null
  sourcePageKey?: string | null
}

export interface IssueCreate extends AnnotationBase {
  category?: IssueCategory
  priority?: IssuePriority
  assignedToUserId?: string | null
  assignedToRole?: string | null
  valueAtCreation?: string | null
  sendNotification?: boolean
}

export interface SelfNoteCreate extends AnnotationBase {
  // Self-notes only need base fields
}

export interface RoleCommentCreate extends AnnotationBase {
  targetRole: string
}

export interface AnnotationUpdate {
  content?: string
  fieldName?: string | null
  category?: IssueCategory
  priority?: IssuePriority
  assignedToUserId?: string | null
  assignedToRole?: string | null
}

export interface StatusChangeRequest {
  newStatus: IssueStatus
  changeReason: string
}

// =============================================================================
// Response Types
// =============================================================================

export interface Annotation {
  id: number
  studyId: string
  annotationType: AnnotationType
  targetRole?: string | null

  // Location
  datasetName: string
  recordKey: Record<string, unknown>
  fieldName?: string | null
  keyColumnsAtCreation: string[]

  // Content
  content: string
  category?: IssueCategory | null
  status?: IssueStatus | null
  priority?: IssuePriority | null

  // Assignment
  assignedToUserId?: string | null
  assignedToRole?: string | null

  // Source location
  sourceModuleKey?: string | null
  sourcePageKey?: string | null
  sourceLocationPath?: string | null

  // Value tracking
  valueAtCreation?: string | null
  dataSnapshot?: Record<string, unknown> | null

  // Read status
  isRead: boolean
  readAt?: string | null

  // Timestamps
  createdAt: string
  updatedAt: string
  resolvedAt?: string | null
  closedAt?: string | null

  // Creator info
  createdBy: string
  creator?: UserBrief | null

  // Counts
  commentCount: number
  attachmentCount: number
  linkedIssueCount: number
}

export interface AnnotationBrief {
  id: number
  annotationType: AnnotationType
  datasetName: string
  recordKey: Record<string, unknown>
  fieldName?: string | null
  content: string
  category?: IssueCategory | null
  status?: IssueStatus | null
  priority?: IssuePriority | null
  assignedToRole?: string | null
  sourceLocationPath?: string | null
  isRead: boolean
  createdAt: string
  createdBy: string
  creator?: UserBrief | null
}

export interface AnnotationListResponse {
  annotations: AnnotationBrief[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

// =============================================================================
// Comment Types
// =============================================================================

export interface CommentCreate {
  content: string
  parentCommentId?: number | null
}

export interface Comment {
  id: number
  annotationId: number
  parentCommentId?: number | null
  content: string
  createdBy: string
  creator?: UserBrief | null
  createdAt: string
  updatedAt: string
  isDeleted: boolean
  replies: Comment[]
}

// =============================================================================
// Attachment Types
// =============================================================================

export interface Attachment {
  id: number
  annotationId: number
  filename: string
  originalFilename: string
  fileSize: number
  mimeType: string
  uploadedBy: string
  uploader?: UserBrief | null
  uploadedAt: string
  isDeleted: boolean
}

export interface AttachmentListResponse {
  attachments: Attachment[]
  total: number
}

// =============================================================================
// Link Types
// =============================================================================

export interface LinkCreate {
  targetAnnotationId: number
  linkType: LinkType
}

export interface AnnotationLink {
  id: number
  sourceAnnotationId: number
  targetAnnotationId: number
  linkType: LinkType
  createdBy: string
  creator?: UserBrief | null
  createdAt: string
  targetAnnotation?: AnnotationBrief | null
}

export interface LinkListResponse {
  links: AnnotationLink[]
  total: number
}

// =============================================================================
// History Types
// =============================================================================

export interface HistoryEntry {
  id: number
  annotationId: number
  action: string
  fieldName?: string | null
  oldValue?: string | null
  newValue?: string | null
  changeReason?: string | null
  changedBy: string
  changer?: UserBrief | null
  createdAt: string
  ipAddress?: string | null
  userAgent?: string | null
}

export interface HistoryListResponse {
  history: HistoryEntry[]
  total: number
}

// =============================================================================
// Value Comparison Types
// =============================================================================

export interface ValueComparison {
  status: 'unchanged' | 'changed' | 'record_not_found' | 'no_snapshot' | 'found' | 'deleted' | 'ambiguous'
  warning?: string | null
  valueAtCreation?: string | null
  dataSnapshotAtCreation?: Record<string, unknown> | null
  keyColumnsAtCreation: string[]
  currentValue?: string | null
  currentDataSnapshot?: Record<string, unknown> | null
  valueChanged: boolean
  snapshotChanges?: Record<string, { old: unknown; new: unknown }> | null
}

// =============================================================================
// Snapshot Comparison Types
// =============================================================================

export interface SnapshotFieldComparison {
  field: string
  creationValue: unknown | null
  currentValue: unknown | null
  changed: boolean
}

export interface SnapshotComparison {
  issueId: number
  datasetName: string
  recordKey: Record<string, unknown>
  createdAt: string
  currentQueryTime: string
  recordStatus: 'found' | 'deleted' | 'multiple_matches' | 'not_found'
  fields: SnapshotFieldComparison[]
  keyColumnsAtCreation: string[]
  currentKeyColumns: string[]
  keyColumnsChanged: boolean
}

// =============================================================================
// Bulk Operations Types
// =============================================================================

export interface BulkStatusChangeRequest {
  annotationIds: number[]
  newStatus: IssueStatus
  changeReason: string
}

export interface BulkAssignRequest {
  annotationIds: number[]
  assignedToUserId?: string | null
  assignedToRole?: string | null
}

export interface BulkMarkReadRequest {
  annotationIds: number[]
}

export interface BulkOperationResponse {
  successCount: number
  failedCount: number
  failedIds: number[]
  errors: string[]
}

// =============================================================================
// Count Types
// =============================================================================

export interface RecordCountRequest {
  datasetName: string
  recordKeys: Record<string, unknown>[]
}

export interface RecordCount {
  recordKey: Record<string, unknown>
  issueCount: number
  openIssueCount: number
  selfNoteCount: number
  roleCommentCounts: Record<string, number>
}

export interface RecordCountResponse {
  counts: RecordCount[]
}

// =============================================================================
// Filter Types
// =============================================================================

export interface AnnotationFilters {
  annotationTypes?: AnnotationType[]
  statuses?: IssueStatus[]
  categories?: IssueCategory[]
  priorities?: IssuePriority[]
  datasetName?: string
  assignedToUserId?: string
  assignedToRole?: string
  createdBy?: string
  isRead?: boolean
  sourceModuleKey?: string
  sourcePageKey?: string
  searchText?: string
  createdAfter?: string
  createdBefore?: string
}

// =============================================================================
// Export Types
// =============================================================================

export interface ExportRequest {
  format: 'csv' | 'pdf'
  filters?: AnnotationFilters
  annotationIds?: number[]
}

// =============================================================================
// Metrics Types
// =============================================================================

export interface AgingBucket {
  bucket: string
  count: number
  percentage: number
}

export interface AgingReport {
  totalOpen: number
  buckets: AgingBucket[]
  averageAgeDays: number
}

export interface IssuesByGroupItem {
  group: string
  totalCount: number
  openCount: number
  resolvedCount: number
  closedCount: number
}

export interface IssuesByGroupResponse {
  items: IssuesByGroupItem[]
  total: number
}

export interface TurnaroundStats {
  averageHours: number
  medianHours: number
  minHours: number
  maxHours: number
  percentile90Hours: number
}

export interface TurnaroundByStatus {
  toStatus: string
  stats: TurnaroundStats
  sampleSize: number
}

export interface TurnaroundReport {
  overallToResolved?: TurnaroundStats | null
  overallToClosed?: TurnaroundStats | null
  byStatus: TurnaroundByStatus[]
  byCategory: Record<string, TurnaroundStats>
  byPriority: Record<string, TurnaroundStats>
}

export interface StatusDistribution {
  status: string
  count: number
  percentage: number
}

export interface CategoryDistribution {
  category: string
  count: number
  percentage: number
}

export interface PriorityDistribution {
  priority: string
  count: number
  percentage: number
}

export interface IssueMetricsSummary {
  totalIssues: number
  openIssues: number
  inReviewIssues: number
  queryRaisedIssues: number
  resolvedIssues: number
  closedIssues: number

  // Percentages
  openPercentage: number
  resolutionRate: number

  // Averages
  averageAgeOpenDays: number
  averageResolutionTimeHours?: number | null

  // Distributions
  byStatus: StatusDistribution[]
  byCategory: CategoryDistribution[]
  byPriority: PriorityDistribution[]

  // Trends
  createdLast7Days: number
  createdLast30Days: number
  resolvedLast7Days: number
  resolvedLast30Days: number
}

export interface UserIssueMetrics {
  myAssignedIssues: number
  myCreatedIssues: number
  unassignedIssues: number
  criticalIssues: number
  highPriorityIssues: number
}

export interface SingleIssueExportData {
  id: number
  studyId: string
  annotationType: string
  datasetName: string
  recordKey: Record<string, unknown>
  fieldName?: string | null
  content: string
  category?: string | null
  status?: string | null
  priority?: string | null
  assignedToRole?: string | null
  assignedToUser?: string | null
  sourceLocationPath?: string | null
  valueAtCreation?: string | null
  currentValue?: string | null
  valueChanged: boolean
  dataSnapshot?: Record<string, unknown> | null
  createdAt: string
  createdByName: string
  updatedAt: string
  resolvedAt?: string | null
  closedAt?: string | null
  history: HistoryEntry[]
  comments: Comment[]
  attachments: Attachment[]
  linkedIssues: AnnotationBrief[]
}
